import { useMemo, useState } from 'react';
import { Button, Checkbox, Divider, Input, Popover, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

export interface FacetedFilterOption {
  label: string;
  value: string;
}

interface FacetedFilterProps {
  title: string;
  options: FacetedFilterOption[];
  value: string[];
  onChange: (values: string[]) => void;
  maxShow?: number;
}

export function FacetedFilter({ title, options, value, onChange, maxShow = 2 }: FacetedFilterProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const showSearch = options.length > 5;

  const filtered = useMemo<FacetedFilterOption[]>(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const toggle = (optValue: string) => {
    if (selectedSet.has(optValue)) {
      onChange(value.filter((v) => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch('');
  };

  const selectedLabels = useMemo(
    () => options.filter((o) => selectedSet.has(o.value)).map((o) => o.label),
    [options, selectedSet],
  );

  const panel = (
    <div style={{ minWidth: 180, maxWidth: 300 }}>
      {showSearch && (
        <Input
          placeholder={`Tìm ${title.toLowerCase()}...`}
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ marginBottom: 6 }}
          autoFocus
        />
      )}
      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <div style={{ color: '#6b6b6b', textAlign: 'center', padding: '8px 0', fontSize: 12 }}>
            Không tìm thấy
          </div>
        ) : (
          filtered.map((opt) => (
            <div
              key={opt.value}
              style={{ padding: '5px 4px', cursor: 'pointer', borderRadius: 4 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              onClick={() => toggle(opt.value)}
            >
              <Checkbox checked={selectedSet.has(opt.value)} style={{ pointerEvents: 'none' }}>
                {opt.label}
              </Checkbox>
            </div>
          ))
        )}
      </div>
      {selectedSet.size > 0 && (
        <>
          <Divider style={{ margin: '6px 0' }} />
          <div
            style={{
              textAlign: 'center',
              cursor: 'pointer',
              color: '#6b6b6b',
              fontSize: 12,
              padding: '2px 0',
              borderRadius: 4,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#333')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#6b6b6b')}
            onClick={() => onChange([])}
          >
            Xoá bộ lọc
          </div>
        </>
      )}
    </div>
  );

  return (
    <Popover
      content={panel}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomLeft"
      arrow={false}
    >
      <Button
        size="small"
        style={{
          height: 28,
          fontSize: 12,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          paddingInline: 8,
        }}
      >
        <PlusOutlined style={{ fontSize: 10 }} />
        {title}
        {selectedSet.size > 0 && (
          <>
            <span
              style={{
                display: 'inline-block',
                width: 1,
                height: 12,
                background: '#d9d9d9',
                margin: '0 2px',
              }}
            />
            {selectedSet.size > maxShow ? (
              <Tag bordered={false} style={{ margin: 0, fontSize: 11, lineHeight: '18px' }}>
                {selectedSet.size} đã chọn
              </Tag>
            ) : (
              selectedLabels.map((label) => (
                <Tag
                  key={label}
                  bordered={false}
                  style={{ margin: 0, fontSize: 11, lineHeight: '18px' }}
                >
                  {label}
                </Tag>
              ))
            )}
          </>
        )}
      </Button>
    </Popover>
  );
}
